import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";

import styles from "./UserManagementTable.module.scss";
import {
  CreditFilterIcon,
  ExportIcon,
  SearchIcon,
  UserBlockIcon,
  UserUnblockIcon,
} from "customIcons";
import { CloseIcon } from "@features/common/SvgIcons";
import FilterDropDown from "@features/creditReports/filterDropdown/FilterDropDown";
import BlockUserModal from "@features/common/modal/blockUserModal/BlockUserModal";
import UnblockUserModal from "@features/common/modal/unBlockUserModal/UnblockUserModal";
import { formatDate, formatTime } from "@utils/common";

// ============================================================================
// Types & Interfaces
// ============================================================================
interface UserManagementTableProps {
  users?: any[];
  isLoading: boolean;
  searchTerm?: string;
  onSearchChange?: (value: string) => void;
  selectedActivity?: string | null;
  onselectedActivityChange?: (value: string | null) => void;
  onBlockUsers?: (userIds: number[]) => Promise<void>;
  onUnblockUsers?: (userIds: number[]) => Promise<void>;
  onExportUsers?: () => void;
  exporting: boolean;
  isBlockLoading?: boolean;
  isUnblockLoading?: boolean;
}

// ============================================================================
// Constants
// ============================================================================
const statusOptions = [
  { label: "All", value: "all" },
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
  { label: "Blocked", value: "blocked" },
];

const UserManagementTable: React.FC<UserManagementTableProps> = ({
  users,
  isLoading,
  searchTerm = "",
  onSearchChange,
  selectedActivity,
  onselectedActivityChange,
  onBlockUsers,
  onUnblockUsers,
  onExportUsers,
  exporting,
  isBlockLoading = false,
  isUnblockLoading = false,
}) => {
  // ============================================================================
  // Local State
  // ============================================================================
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [BlockUserModalOpen, setBlockUserModalOpen] = useState(false);
  const [unblockUserModalOpen, setUnblockUserModalOpen] = useState(false);
  const [userIds, setUserIds] = useState(null);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  /**
   * Handles mouse enter event for status filter dropdown
   * Clears any pending close timeout and opens the dropdown
   */
  const handleMouseEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
  }, []);

  /**
   * Handles mouse leave event for status filter dropdown
   * Sets a timeout to close the dropdown after 300ms
   */
  const handleMouseLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 300);
  }, []);

  const handleOpenBlockModal = (id: any) => {
    setUserIds(id);
    setBlockUserModalOpen(true);
  };

  const handleCloseBlockModal = () => {
    setBlockUserModalOpen(false);
  };

  const handleOpenunblockModal = (id: any) => {
    setUserIds(id);
    setUnblockUserModalOpen(true);
  };

  const handleCloseunblockModal = () => {
    setUnblockUserModalOpen(false);
  };

  /**
   * Handles blocking selected users
   */
  const handleBlockUsers = async (userId: number[]) => {
    try {
      await onBlockUsers?.(userId);
      handleCloseBlockModal();
    } catch (error) {
      console.error("Failed to block users:", error);
      handleCloseBlockModal();
    }
  };

  /**
   * Handles unblocking selected users
   */
  const handleUnblockUsers = async (userId: number[]) => {
    try {
      await onUnblockUsers?.(userId);
      handleCloseunblockModal();
    } catch (error) {
      handleCloseunblockModal();
    }
  };

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className={`${styles["reports-table"]} users-table`}>
      {/* Action Bar with Filters */}
      <div className={styles["reports-table__action-bar"]}>
        <div className={styles["reports-table__action-bar__left"]}></div>
        <div className={styles["reports-table__action-bar__right"]}>
          {/* Search Input */}
          <div className={styles["reports-table__action-bar__right__search"]}>
            <SearchIcon />
            <input
              type="text"
              placeholder="Search by User ID/Name/Email/Mobile"
              className={
                styles["reports-table__search-bar-container__inner-input"]
              }
              value={searchTerm}
              onChange={(e) => onSearchChange?.(e.target.value)}
            />
            {searchTerm && (
              <div
                className={
                  styles["reports-table__action-bar__right__search__close-icon"]
                }
                onClick={() => onSearchChange?.("")}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  width: "20px",
                }}
              >
                <CloseIcon />
              </div>
            )}
          </div>

          {/* Activity Filter Dropdown */}
          <div
            className={styles["reports-table__action-bar__right__filter"]}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <CreditFilterIcon />
            <span>
              {selectedActivity && selectedActivity !== "all"
                ? statusOptions.find(
                    (option) => option.value === selectedActivity,
                  )?.label
                : "Filter by Status"}
            </span>
            {isOpen && (
              <FilterDropDown
                options={statusOptions}
                selectedValue={selectedActivity}
                onSelect={onselectedActivityChange}
                setIsOpen={setIsOpen}
              />
            )}
            {selectedActivity && selectedActivity !== "all" && (
              <div
                className={
                  styles["reports-table__action-bar__right__filter__close-icon"]
                }
                onClick={() => {
                  setIsOpen(false);
                  onselectedActivityChange?.("all");
                }}
              >
                <CloseIcon />
              </div>
            )}
          </div>

          {/* Export Button */}
          <div
            className={styles["reports-table__action-bar__right__export"]}
            onClick={onExportUsers}
          >
            {exporting ? (
              <CircularProgress size={16} color="info" />
            ) : (
              <ExportIcon />
            )}
            <span>Export</span>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div
        className={styles["reports-table__table-wrapper"]}
        style={
          isLoading
            ? { height: "393px", position: "relative" }
            : { maxHeight: "393px", overflowY: "auto", position: "relative" }
        }
      >
        <TableContainer sx={{ marginTop: "16px", padding: "0 8px" }}>
          <Table
            className={`${styles["reports-table__table"]} user-man-table`}
            stickyHeader
          >
            {/* Table Header */}
            <TableHead>
              <TableRow
                className={styles["reports-table__head-row"]}
                sx={{
                  "& th": {
                    backgroundColor: "#F5F5F5",
                    zIndex: 1,
                    padding: "0px 16px",
                    borderBottom: "none",
                  },
                }}
              >
                <TableCell>User ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Date Added</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            {/* Table Body with Loading State */}
            {isLoading ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: "none" }}>
                    <CircularProgress
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                      }}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {users?.map((user, index) => (
                  <TableRow
                    key={user?.id ?? index}
                    className={styles["reports-table__body-row"]}
                    sx={{ height: "84px" }}
                  >
                    {/* User ID */}
                    <TableCell>{user?.id}</TableCell>

                    {/* Name */}
                    <TableCell>{user?.name}</TableCell>

                    {/* Email */}
                    <TableCell>{user?.email}</TableCell>

                    {/* Mobile */}
                    <TableCell>{user?.mobile}</TableCell>

                    {/* Date Added */}
                    <TableCell>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span>{formatDate(user?.created_on)}</span>
                        <span style={{ color: "#868785", fontWeight: "500" }}>
                          {formatTime(user?.created_on, "Asia/Dubai", {
                            ampm: false,
                          })}{" "}
                          UAE
                        </span>
                      </div>
                    </TableCell>
                    {/* Status */}
                    <TableCell
                      sx={{
                        color:
                          user?.status === "Blocked"
                            ? "#FF3A44 !important"
                            : user?.status === "Active"
                              ? "#34C759 !important"
                              : "#868785 !important",
                      }}
                    >
                      {user?.status}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip
                        title={
                          user?.status === "Blocked"
                            ? "Unblock User"
                            : "Block User"
                        }
                        arrow
                        placement="bottom"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: "#000",
                              color: "#fff",
                              fontSize: "10px",
                              padding: "4px 8px",
                              borderRadius: "4px",
                            },
                          },
                          arrow: { sx: { color: "#000" } },
                        }}
                      >
                        <div
                          onClick={
                            user?.status === "Blocked"
                              ? () => handleOpenunblockModal(user?.id)
                              : () => handleOpenBlockModal(user?.id)
                          }
                          style={{ cursor: "pointer" }}
                        >
                          {user?.status === "Blocked" ? (
                            <UserUnblockIcon />
                          ) : (
                            <UserBlockIcon />
                          )}
                        </div>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {BlockUserModalOpen && (
                  <BlockUserModal
                    onClose={handleCloseBlockModal}
                    isSubmitting={isBlockLoading}
                    onSubmit={handleBlockUsers}
                    userId={userIds}
                  />
                )}
                {unblockUserModalOpen && (
                  <UnblockUserModal
                    onClose={handleCloseunblockModal}
                    isSubmitting={isUnblockLoading}
                    onSubmit={handleUnblockUsers}
                    userId={userIds}
                  />
                )}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default UserManagementTable;
